import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Box,
  Checkbox,
} from "@mui/material";

// Define your types for records
interface PaymentRecord {
  id: number;
  borrowerName: string;
  paymentNumber: number;
  paymentDate: string;
  repaymentAmount: number;
  paymentStatus: string;
}

interface PaymentTableProps {
  records: PaymentRecord[];
  onUpdateStatus: (id: number, status: string) => void;
}

const PaymentTable: React.FC<PaymentTableProps> = ({ records, onUpdateStatus }) => {
  const [updatedRecords, setUpdatedRecords] = useState<PaymentRecord[]>(records);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<string>("unpaid");

  const handleStatusChange = (id: number, status: string) => {
    const updatedRecord = updatedRecords.map((record) =>
      record.id === id ? { ...record, paymentStatus: status } : record
    );
    setUpdatedRecords(updatedRecord);
    onUpdateStatus(id, status);
  };

  const handleBulkStatusChange = () => {
    const updatedRecordsWithBulkStatus = updatedRecords.map((record) => {
      if (selectedIds.includes(record.id)) {
        return { ...record, paymentStatus: bulkStatus };
      }
      return record;
    });

    setUpdatedRecords(updatedRecordsWithBulkStatus);
    selectedIds.forEach((id) => onUpdateStatus(id, bulkStatus));
    setSelectedIds([]); // Clear selection after bulk update
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(updatedRecords.map((record) => record.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "unpaid":
        return "red"; // Color for unpaid status
      case "paid":
        return "green"; // Color for paid status
      case "pending":
        return "orange"; // Color for pending status
      default:
        return "black"; // Default color
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch("https://ultra-ubumwe-apis-app.azurewebsites.net/api/repayment/status/updated", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedRecords),
      });

      if (!response.ok) {
        throw new Error("Failed to submit data");
      }

      const result = await response.json();
      console.log("Data submitted successfully:", result);
      // You can handle success feedback here (e.g., show a success message)

    } catch (error) {
      console.error("Error submitting data:", error);
      // Handle error feedback here (e.g., show an error message)
    }
  };

  return (
    <Box sx={{ padding: 2, overflowX: "auto" }}>
      <Typography variant="h4" gutterBottom textAlign="center">
        Payment Management
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="bulk-status-label">Bulk Status</InputLabel>
        <Select
          labelId="bulk-status-label"
          value={bulkStatus}
          onChange={(e) => setBulkStatus(e.target.value as string)}
        >
          <MenuItem value="unpaid">Unpaid</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        sx={{ mb: 2 }}
        onClick={handleBulkStatusChange}
        disabled={selectedIds.length === 0} // Disable button if no records selected
      >
        Apply Bulk Status
      </Button>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox
                  checked={selectedIds.length === updatedRecords.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "#1976d2",
                  color: "white",
                }}
              >
                Payment Number
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "#1976d2",
                  color: "white",
                }}
              >
                Borrower Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "#1976d2",
                  color: "white",
                }}
              >
                Payment Date
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "#1976d2",
                  color: "white",
                }}
              >
                Repayment Amount
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "#1976d2",
                  color: "white",
                }}
              >
                Payment Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {updatedRecords.map((record) => (
              <TableRow key={record.id} sx={{ backgroundColor: "#CBB10C" }}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.includes(record.id)}
                    onChange={() => handleSelectRow(record.id)}
                  />
                </TableCell>
                <TableCell>{record.paymentNumber}</TableCell>
                <TableCell>{record.borrowerName}</TableCell>
                <TableCell>{record.paymentDate}</TableCell>
                <TableCell>${record.repaymentAmount.toFixed(2)}</TableCell>
                <TableCell sx={{ color: getStatusColor(record.paymentStatus) }}>
                  <FormControl fullWidth>
                    <InputLabel id={`status-label-${record.id}`}>Status</InputLabel>
                    <Select
                      labelId={`status-label-${record.id}`}
                      value={record.paymentStatus}
                      onChange={(e) =>
                        handleStatusChange(record.id, e.target.value as string)
                      }
                    >
                      <MenuItem value="unpaid">Unpaid</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button variant="contained" color="secondary" sx={{ ml: 1 }} onClick={handleSubmit}>
          Submit
        </Button>
      </Box>
    </Box>
  );
};

const RepaymentStatus: React.FC = () => {
  const [records, setRecords] = useState<PaymentRecord[]>([]);  // Initialize with an empty array
  const [loading, setLoading] = useState(true); // Add loading state

  useEffect(() => {
    // Fetch records from an API endpoint
    const fetchData = async () => {
      try {
        const response = await fetch("https://ultra-ubumwe-apis-app.azurewebsites.net/api/repayment/status"); // Replace with your API
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const data = await response.json();
        setRecords(data);  // Assuming the API response is an array of PaymentRecord
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);  // Stop loading when data is fetched or error occurs
      }
    };

    fetchData();
  }, []);  // Empty dependency array to fetch data once on component mount

  // Function to update the payment status
  const handleUpdateStatus = (id: number, status: string) => {
    setRecords((prevRecords) =>
      prevRecords.map((record) =>
        record.id === id ? { ...record, paymentStatus: status } : record
      )
    );
  };

  // Show loading message until data is fetched
  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return <PaymentTable records={records} onUpdateStatus={handleUpdateStatus} />;
};

export default RepaymentStatus;
